import { S as Swiper, N as Navigation } from './navigation-_PFp8ZJV.js';
import { M as Mousewheel } from './mousewheel-BGHxKZAZ.js';
import { d as debounce } from './debounce-CFN_LA0O.js';

class ProductMediaSwiper extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.bundleItems = [];
    this.addOnItems = [];

    // Debounce function
    this.debouncedUpdateSlideVisibility = debounce(this.updateSlideVisibility, 100, {
      });

    // Initialize the swiper
    this.initThumbSwiper();
    this.initSwiper();

    // Listen to product changes
    this.unsubscribeProductChange = subscribe(`product:changed:${this.sectionId}`, ({ html }) => {
      this.swapMedia(html);
    });

    // listen to variant changes
    this.unsubscribeVariantChange = subscribe(`variant:changed:${this.sectionId}`, ({ html }) => {
      this.swapMedia(html);
    });

    // Listen to bundle variant changes
    this.unsubscribeBundleVariantChange = subscribe(
      `bundle:variant:changed:${this.sectionId}`,
      ({ variant, group }) => {
        this.lock = true;
        this.setBundleItems(variant, group);
        this.debouncedUpdateSlideVisibility();
        setTimeout(() => {
          this.lock = false;
        }, 100);
      }
    );

    // Listen to add-on variant changes
    this.unsubscribeAddOnVariantChange = subscribe(
      `add-on:variant:changed:${this.sectionId}`,
      ({ variant, group }) => {
        this.lock = true;
        this.setAddOnItems(variant, group);
        this.debouncedUpdateSlideVisibility();
        setTimeout(() => {
          this.lock = false;
        }, 100);
      }
    );

    // If there are no bundle or add-on items, update slide visibility immediately
    if (
      !document.querySelector('add-on-variant-select') &&
      !document.querySelector('bundle-variant-select')
    ) {
      this.debouncedUpdateSlideVisibility();
    }
  }

  disconnectedCallback() {
    this.unsubscribeProductChange();
    this.unsubscribeVariantChange();
    this.unsubscribeBundleVariantChange();
    this.unsubscribeAddOnVariantChange();
  }

  /**
   * Initialize the main swiper
   */
  initSwiper() {
    this.mainSwiper = new Swiper(this.querySelector('.swiper--media'), {
      modules: [Navigation, Mousewheel],
      spaceBetween: 16,
      mousewheel: true,
      initialSlide: this.getIndex(),
      on: {
        afterInit: (swiper) => {
          if (this.lock) return;
          this.setIndex(swiper.activeIndex);
        },
        slideChange: (swiper) => {
          if (this.lock) return;
          this.setIndex(swiper.activeIndex);
        },
      },
      // Don't use thumbs because we have hidden slides
    });
  }

  /**
   * Swap in fresh media from a re-rendered section without tearing down Swiper.
   * Keeps both Swiper instances (and their bound handlers) alive to avoid the
   * teardown/re-init flash, then re-applies the live bundle/add-on visibility.
   * @param {Document} html - Parsed section HTML.
   */
  swapMedia(html) {
    const next = html.querySelector('product-media-swiper');
    const mainWrapper = this.querySelector('.swiper--media .swiper-wrapper');
    const thumbsWrapper = this.querySelector('.swiper--thumbs .swiper-wrapper');
    const nextMain = next?.querySelector('.swiper--media .swiper-wrapper');
    const nextThumbs = next?.querySelector('.swiper--thumbs .swiper-wrapper');

    // Fallback to the previous behavior if the structure is unexpected.
    if (!mainWrapper || !thumbsWrapper || !nextMain || !nextThumbs) {
      if (next) this.replaceWith(next);
      return;
    }

    this.lock = true;

    // Replace only the slides; the Swiper containers + navigation stay put.
    mainWrapper.replaceChildren(...nextMain.children);
    thumbsWrapper.replaceChildren(...nextThumbs.children);

    // Re-index Swiper against the new slides.
    this.mainSwiper.update();
    this.thumbSwiper.update();

    // Re-apply bundle/add-on visibility and reconcile the active slide index.
    this.updateSlideVisibility();

    this.lock = false;
  }

  /**
   * Initialize the thumbnail swiper
   */
  initThumbSwiper() {
    this.thumbSwiper = new Swiper(this.querySelector('.swiper--thumbs'), {
      modules: [Navigation, Mousewheel],
      spaceBetween: 8,
      slidesPerView: 'auto',
      watchSlidesProgress: true,
      mousewheel: true,
      navigation: {
        nextEl: '.swiper-thumbs-button-next',
        prevEl: '.swiper-thumbs-button-prev',
      },
      on: {
        // Click on a thumbnail to open the corresponding media slide
        // Don't use thumbs because we have hidden slides
        click: (swiper, event) => {
          const slide = event.target.closest('.swiper-slide');
          if (!slide) return;
          const id = slide.dataset.id;
          const index = Array.from(
            this.querySelectorAll('.swiper--thumbs .swiper-slide:not(.hidden)')
          ).findIndex((slide) => slide.dataset.id === id);
          if (index <= -1) return;
          this.mainSwiper.slideTo(index);
        },
      },
    });
  }

  /**
   * Update the slide visibility
   */
  updateSlideVisibility() {
    // Get all slides
    const slides = this.querySelectorAll('.swiper--media .swiper-slide[data-media-logic]');

    // If we show assembled media then hide the variant media
    let shouldHideVariantMedia = false;

    // Loop through all slides
    slides.forEach((slide) => {
      const mediaLogic = JSON.parse(slide.dataset.mediaLogic);
      const id = slide.dataset.id;

      // Check if one of the logic values is in the bundle items or add-on items, else hidden is false
      const mediaLogicMap = mediaLogic.map((logic) => {
        let visible = false;
        if (logic.type === 'bundle') {
          logic.values.map((value) => {
            this.bundleItems.map((item) => {
              if (item.variant.options.includes(value)) {
                visible = true;
              }
            });
          });
        } else if (logic.type === 'add-on') {
          logic.values.map((value) => {
            this.addOnItems.map((item) => {
              if (item.variant.options.includes(value)) {
                visible = true;
              }
            });
          });
        }
        return {
          ...logic,
          visible,
        };
      });

      // Check if the mediaLogicMap has a visible false
      const shouldHide = mediaLogicMap.some((logic) => !logic.visible);

      // Toggle the hidden class on the slide and the thumbnail slide
      if (shouldHide) {
        slide.classList.add('hidden');
        this.thumbSwiper.slides.find((slide) => slide.dataset.id === id).classList.add('hidden');
      } else {
        shouldHideVariantMedia = true;
        slide.classList.remove('hidden');
        this.thumbSwiper.slides.find((slide) => slide.dataset.id === id).classList.remove('hidden');
      }
    });

    // Update the variant media by checking if the assembled media is visible
    this.querySelectorAll('.swiper--media .swiper-slide[data-variant-media]').forEach((slide) => {
      slide.classList.toggle('hidden', shouldHideVariantMedia);
      const id = slide.dataset.id;
      this.thumbSwiper.slides
        .find((slide) => slide.dataset.id === id)
        .classList.toggle('hidden', shouldHideVariantMedia);
    });

    // Update the thumbnail swiper
    this.mainSwiper.update();
    this.thumbSwiper.update();

    // Update the slide index
    const index = this.mainSwiper.activeIndex;
    const newIndex = this.getIndex();
    if (index !== newIndex) {
      this.setIndex(newIndex);
      this.mainSwiper.slideTo(newIndex, 0, false);
    }
  }

  /**
   * Set the bundle items
   * @param {Object} variant
   * @param {string} group
   */
  setBundleItems(variant, group) {
    // Remove the item from the same group
    this.bundleItems = this.bundleItems.filter((item) => item.group !== group);

    // Return if 'none' variant, if no variant is found we should show a sold out message
    if (variant === 'none') {
      return;
    }

    // Add the bundle item to the bundle items array or set the item to null if no variant is found
    const item = {
      group,
      variant,
    };

    this.bundleItems = [...this.bundleItems, item];
  }

  /**
   * Set the add-on items
   * @param {Object} variant
   * @param {string} group
   */
  setAddOnItems(variant, group) {
    // Remove the item from the same group
    this.addOnItems = this.addOnItems.filter((item) => item.group !== group);

    // Return if 'none' variant, if no variant is found we should show a sold out message
    if (variant === 'none') {
      return;
    }

    // Add the add-on item to the add-on items array or set the item to null if no variant is found
    const item = {
      group,
      variant,
    };

    this.addOnItems = [...this.addOnItems, item];
  }

  /**
   * Save the index of the active slide per product group in the session storage
   * @param {*} index
   */
  setIndex(index) {
    this.index = index;
    sessionStorage.setItem(`product-media-swiper-index-${this.sectionId}`, index);
  }

  /**
   * Get the index of the active slide per product group from the session storage
   * @returns {number}
   */
  getIndex() {
    let index = parseInt(sessionStorage.getItem(`product-media-swiper-index-${this.sectionId}`));
    const numberOfSlides = this.querySelectorAll(
      '.swiper--media .swiper-slide:not(.hidden)'
    ).length;
    if (index > numberOfSlides - 1 || !index) {
      // Find the first non-hidden slide index
      index =
        [...this.querySelectorAll('.swiper--media .swiper-slide:not(.hidden)')].findIndex(
          () => true
        ) || 0;
    }
    return index;
  }
}

customElements.define('product-media-swiper', ProductMediaSwiper);
